import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../../theme/utils';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../../theme/types';
import { Tooltip } from '@prio365/prio365-react-library';
import { LinkComponent } from '../DashboardEditWidgetLinkContentDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../components/Flex';
import DashboardContext from '../../context/dashboardContext';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    overflowX: 'auto',
  },
  linkContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.large,
    padding: theme.spacing.small,
    color: theme.colors.application.typography.default,
    '&:hover': {
      color: theme.colors.application.typography.active,
      cursor: 'pointer',
    },
  },
  header: {
    fontSize: theme.font.fontSize.large,
    fontWeight: theme.font.fontWeight.bold,
  },
  noLinks: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.small,
  },
}));

interface DashboardLinksItemProps {
  className?: string;
  dashboardItemId?: string;
}

export const DashboardLinksItem: React.FC<DashboardLinksItemProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { className, dashboardItemId } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const { initDashboardItems } = useContext(DashboardContext);

  let links: LinkComponent[] = [];
  let title = t('dashboard:links.title');

  try {
    const raw = initDashboardItems?.find(
      (item) =>
        item.dashboardItemId === dashboardItemId && item.type === 'links'
    )?.data;

    if (!raw) {
      console.warn('Kein jsonContent vorhanden');
    } else {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (parsed?.links && Array.isArray(parsed.links)) {
        links = parsed.links;
        if (parsed.title) {
          title = parsed.title;
        }
      } else {
        console.warn('Ungültige Struktur im jsonContent');
      }
    }
  } catch (e) {
    console.error('Fehler beim Parsen von jsonContent:', e);
  }

  //#endregion

  return (
    <Flex.Column
      className={classNames(classes.root, className)}
      childrenGap={24}
    >
      <Flex.Row alignItems="center" childrenGap={theme.spacing.small}>
        <div className={classes.header} style={{ margin: 0 }}>
          {title}
        </div>
      </Flex.Row>
      <Flex.Column childrenGap={theme.spacing.small}>
        {links.map((link) => (
          <InnerLink key={link.id} link={link} />
        ))}
        {links.length === 0 && (
          <Flex.Row justifyContent="center" className={classes.noLinks}>
            <span>{t('dashboard:links.noLinks')}</span>
          </Flex.Row>
        )}
      </Flex.Column>
    </Flex.Column>
  );
};

export default DashboardLinksItem;

interface InnerLinkProps {
  className?: string;
  link: LinkComponent;
}

export const InnerLink: React.FC<InnerLinkProps> = (props) => {
  //#region ------------------------------ Defaults
  const { className, link } = props;
  const classes = useStyles();

  const href = link.url.match(/^https?:\/\//)
    ? link.url
    : `https://${link.url}`;
  //#endregion

  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      <div className={classNames(className, classes.linkContainer)}>
        <FontAwesomeIcon icon={['fal', 'link']} size="lg" />
        <Flex.Item flex={1}>{link.title}</Flex.Item>
        {link.description && (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Tooltip overlay={link.description} placement="top">
              <FontAwesomeIcon icon={['fal', 'circle-info']} />
            </Tooltip>
          </div>
        )}
      </div>
    </a>
  );
};
